import { forwardRef, useEffect, useId } from "react";
import FlowbiteDatepicker from "flowbite-datepicker/Datepicker";
import { useTranslation } from "react-i18next";
import { CalendarIcon, CrossIcon } from "@components/icons";
import { Input, type IInputProps } from "@components/inputs/Input/Input";
import { isNullOrUndefined } from "@utils/common";
import { formatDate } from "@utils/units";
import { Language } from "@/i18n";
// TODO: Fix temporary solution. Not fully supported according to docs - https://flowbite.com/docs/components/datepicker/#language-i18n
import enGB from "./locales/en-GB";
import et from "./locales/et";
import lt from "./locales/lt";
import pl from "./locales/pl";
import ru from "./locales/ru";

// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
Object.assign(FlowbiteDatepicker.locales, lt, enGB, et, ru, pl);

const getFlowbiteLanguage = (languageCode: Language) => {
  switch (languageCode) {
    case Language.LT:
      return "lt";
    case Language.RU:
      return "ru";
    case Language.EE:
      return "et";
    case Language.PL:
      return "pl";
    default:
      return "enGB";
  }
};

interface IDatepickerProps extends Omit<IInputProps, "onChange" | "value"> {
  onChange: (date: Date | undefined | null) => void;
  value: Date | undefined;
}

export const Datepicker = forwardRef<HTMLInputElement, IDatepickerProps>(
  (props, ref) => {
    const { i18n } = useTranslation();
    const { onChange, value, ...inputProps } = props;
    const inputId = useId();

    const selectDate = (dateValue: string | undefined) => {
      if (isNullOrUndefined(dateValue)) {
        onChange?.(null);
        return;
      }

      const date = new Date(dateValue);
      const value = !isNaN(date.getTime()) ? date : null;
      onChange?.(value);
    };

    useEffect(() => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call
      const datepicker = new FlowbiteDatepicker(
        document.getElementById(inputId),
        {
          format: "yyyy-mm-dd",
          autohide: true,
          weekStart: 1,
          // pickLevel: 1,
          language: getFlowbiteLanguage(i18n.language as Language),
        },
      );
      return () => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
        datepicker?.destroy?.();
      };
    }, [inputId, i18n]);

    return (
      <Input
        iconLeft={CalendarIcon}
        {...inputProps}
        id={inputId}
        ref={ref}
        readOnly={true}
        value={!isNullOrUndefined(value) ? formatDate(value) : ""}
        onBlur={(e) => selectDate(e?.target?.value)}
        {...(!isNullOrUndefined(value)
          ? {
              iconRight: CrossIcon,
              onRightIconClick: () => {
                selectDate(undefined);
              },
            }
          : {})}
        className="z-[9999]"
      />
    );
  },
);
